/**
 * CONSAI Business App — Profile & Settings Page
 * Redesigned: Executive dark card aesthetic per user design prompt
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Switch } from '../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  User, Crown, Clock, Lock, Eye, EyeOff, Loader2, Save, Bell, Globe,
  RocketIcon
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import MobileNav from '../components/MobileNav';
import { AetherPage } from '../components/AetherPage';
import TeamMembers from '../components/TeamMembers';
import QRCodeManager from '../components/QRCodeManager';
import BankDetails from '../components/BankDetails';

/* ── Shared input style ─────────────────────────────────── */
const DarkInput = ({ label, type = 'text', value, onChange, placeholder, readOnly, disabled, testId, hint }) => (
  <div className="space-y-2">
    <label className="block text-xs font-bold tracking-widest uppercase text-[#8A8A93]">{label}</label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      readOnly={readOnly}
      disabled={disabled}
      data-testid={testId}
      className={`w-full p-3 rounded-lg text-sm text-white outline-none transition-all ${
        readOnly || disabled
          ? 'bg-[#050505] border border-[#1A1A1A] text-[#8A8A93] cursor-not-allowed'
          : 'bg-[#050505] border border-[#262626] focus:border-white'
      }`}
    />
    {hint && <p className="text-xs text-[#8A8A93]">{hint}</p>}
  </div>
);

/* ── Executive Card wrapper ─────────────────────────────── */
const ExecCard = ({ children, className = '', glow }) => (
  <div
    className={`rounded-xl p-6 md:p-8 ${className}`}
    style={{
      background: '#111111',
      border: '1px solid #262626',
      boxShadow: glow ? '0 0 30px rgba(98,255,150,0.06)' : 'none',
    }}
  >
    {children}
  </div>
);

/* ── Notification Toggle Row ───────────────────────────── */
const NotifRow = ({ label, sub, checked, onChange, disabled, testId }) => (
  <div className={`flex items-center justify-between py-4 border-b border-[#1A1A1A] last:border-0 ${disabled ? 'opacity-50' : ''}`}>
    <div>
      <p className="text-sm font-semibold text-[#F4F4F5]">{label}</p>
      <p className="text-xs text-[#8A8A93] mt-0.5">{sub}</p>
    </div>
    <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} data-testid={testId} />
  </div>
);

/* ═══════════════════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════════════════ */
const Profile = () => {
  const { t, language, setLanguage, translations } = useLanguage();
  const { user, updateProfile, changePassword, logout } = useAuth();

  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    business_name: user?.business_name || '',
    business_type: user?.business_type || '',
    gst_number: user?.gst_number || '',
    location_city: user?.location_city || '',
    location_state: user?.location_state || '',
    business_logo_url: user?.business_logo_url || ''
  });

  const [notifications, setNotifications] = useState({
    email_notifications: user?.notification_preferences?.email_notifications ?? true,
    whatsapp_notifications: user?.notification_preferences?.whatsapp_notifications ?? false,
    low_stock_alerts: user?.notification_preferences?.low_stock_alerts ?? false,
    payment_reminders: user?.notification_preferences?.payment_reminders ?? false,
    daily_summary: user?.notification_preferences?.daily_summary ?? false,
  });

  const [passwordData, setPasswordData] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [showPasswords, setShowPasswords] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  const navigate = useNavigate();
  const isPremium = user?.current_plan === 'premium' || user?.current_plan === 'premium_trial';

  const businessTypes = Object.entries(translations.businessTypes || {}).map(([, value]) => ({
    value: value.en, label: language === 'hi' ? value.hi : value.en,
  }));
  const indianStates = Object.entries(translations.states || {}).map(([, value]) => ({
    value: value.en, label: language === 'hi' ? value.hi : value.en,
  }));

  const handleProfileChange = (field, value) => setProfileData(prev => ({ ...prev, [field]: value }));

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await updateProfile({ ...profileData, notification_preferences: notifications, language_preference: language === 'hi' ? 'hindi' : 'english' });
      toast.success(t('profileUpdated'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('somethingWentWrong'));
    } finally { setSavingProfile(false); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordData.new_password.length < 8) { toast.error(t('passwordTooShort')); return; }
    if (passwordData.new_password !== passwordData.confirm_password) { toast.error(t('passwordsMismatch')); return; }
    setSavingPassword(true);
    try {
      await changePassword(passwordData.current_password, passwordData.new_password);
      toast.success(t('passwordChanged'));
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('somethingWentWrong'));
    } finally { setSavingPassword(false); }
  };

  const getDaysRemaining = () => {
    if (!user?.plan_expiry_date) return 0;
    return Math.max(0, Math.ceil((new Date(user.plan_expiry_date) - new Date()) / (1000 * 60 * 60 * 24)));
  };

  const planLabel = user?.current_plan === 'premium' ? 'Premium'
    : user?.current_plan === 'premium_trial' ? `Trial (${getDaysRemaining()} days)` : 'Starter';

  const planFeatures = [
    { label: language === 'hi' ? 'AI-संचालित पूर्वानुमान' : 'AI-Powered Forecasting' },
    { label: language === 'hi' ? '24/7 प्राथमिकता समर्थन' : '24/7 Priority Support' },
    { label: language === 'hi' ? 'असीमित टीम सदस्य' : 'Unlimited Team Members' },
  ];

  const profileSaveBtn = (
    <button
      onClick={handleSaveProfile}
      disabled={savingProfile}
      className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-white text-[#0A0A0B] text-xs font-bold tracking-wider uppercase hover:bg-white/90 active:scale-95 transition-all disabled:opacity-50"
      data-testid="save-profile-btn"
    >
      {savingProfile ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
      {language === 'hi' ? 'सेव करें' : 'Save Changes'}
    </button>
  );

  return (
    <AetherPage
      title={language === 'hi' ? 'प्रोफ़ाइल और सेटिंग्स' : 'Profile & Settings'}
      icon={User}
      iconColor="#ffffff"
      titleTestId="profile-title"
      rightElement={profileSaveBtn}
      mainClassName="px-4 md:px-8 py-6 md:py-8 space-y-8"
    >
          {/* ── Business Profile + Subscription ── */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Business Profile — 8 cols */}
            <ExecCard className="lg:col-span-8">
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-lg font-bold text-[#F4F4F5]">
                  {language === 'hi' ? 'व्यावसायिक प्रोफ़ाइल' : 'Business Profile'}
                </h3>
                <div className="text-xs text-[#8A8A93] bg-[#1A1A1A] border border-[#262626] px-3 py-1 rounded">
                  ID: {user?.unique_client_id || 'CS-00000'}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <DarkInput
                  label={language === 'hi' ? 'मालिक का नाम' : 'Owner Name'}
                  value={profileData.name}
                  onChange={(e) => handleProfileChange('name', e.target.value)}
                  testId="profile-name-input"
                />
                <DarkInput
                  label={language === 'hi' ? 'व्यवसाय का नाम' : 'Business Name'}
                  value={profileData.business_name}
                  onChange={(e) => handleProfileChange('business_name', e.target.value)}
                  placeholder={language === 'hi' ? 'व्यवसाय का नाम' : 'Your business name'}
                  testId="profile-business-name-input"
                />

                {/* Business Type */}
                <div className="space-y-2">
                  <label className="block text-xs font-bold tracking-widest uppercase text-[#8A8A93]">
                    {language === 'hi' ? 'व्यवसाय प्रकार' : 'Business Type'}
                  </label>
                  <Select value={profileData.business_type} onValueChange={(v) => handleProfileChange('business_type', v)}>
                    <SelectTrigger
                      className="bg-[#050505] border-[#262626] text-white h-12"
                      data-testid="profile-business-type-select"
                    >
                      <SelectValue placeholder={language === 'hi' ? 'प्रकार चुनें' : 'Select type'} />
                    </SelectTrigger>
                    <SelectContent>
                      {businessTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <DarkInput
                  label={`${language === 'hi' ? 'GST नंबर' : 'GST Number'} (${language === 'hi' ? 'वैकल्पिक' : 'optional'})`}
                  value={profileData.gst_number}
                  onChange={(e) => handleProfileChange('gst_number', e.target.value)}
                  placeholder="27AAAAA0000A1Z5"
                  testId="profile-gst-input"
                />
                <DarkInput
                  label={language === 'hi' ? 'शहर' : 'City'}
                  value={profileData.location_city}
                  onChange={(e) => handleProfileChange('location_city', e.target.value)}
                  placeholder={language === 'hi' ? 'शहर का नाम' : 'City name'}
                  testId="profile-city-input"
                />

                {/* State */}
                <div className="space-y-2">
                  <label className="block text-xs font-bold tracking-widest uppercase text-[#8A8A93]">
                    {language === 'hi' ? 'राज्य' : 'State'}
                  </label>
                  <Select value={profileData.location_state} onValueChange={(v) => handleProfileChange('location_state', v)}>
                    <SelectTrigger className="bg-[#050505] border-[#262626] text-white h-12" data-testid="profile-state-select">
                      <SelectValue placeholder={language === 'hi' ? 'राज्य चुनें' : 'Select state'} />
                    </SelectTrigger>
                    <SelectContent>
                      {indianStates.map((state) => (
                        <SelectItem key={state.value} value={state.value}>{state.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <DarkInput
                  label={language === 'hi' ? 'ईमेल' : 'Email'}
                  value={user?.email || ''}
                  readOnly
                  hint={language === 'hi' ? 'ईमेल बदलने के लिए सपोर्ट से संपर्क करें' : 'Contact support to change email'}
                />
                <DarkInput
                  label={language === 'hi' ? 'मोबाइल नंबर' : 'Mobile Number'}
                  value={user?.mobile_number || ''}
                  readOnly
                  hint={language === 'hi' ? 'मोबाइल बदलने के लिए सपोर्ट से संपर्क करें' : 'Contact support to change mobile'}
                />
              </div>
            </ExecCard>

            {/* Subscription — 4 cols */}
            <ExecCard className="lg:col-span-4" glow>
              <div className="flex items-center gap-2 mb-4">
                <Crown className="h-5 w-5 text-[#62FF96]" />
                <span className="text-xs font-bold tracking-widest uppercase text-[#8A8A93]">
                  {language === 'hi' ? 'वर्तमान प्लान' : 'Current Plan'}
                </span>
              </div>

              <h4 className="text-4xl font-bold text-white mb-2">{planLabel}</h4>
              <p className="text-sm text-[#8A8A93] mb-6">
                {isPremium
                  ? (language === 'hi' ? 'अनलिमिटेड AI, एडवांस्ड एनालिटिक्स।' : 'Advanced analytics and unlimited team members.')
                  : (language === 'hi' ? 'प्रीमियम में अपग्रेड करें।' : 'Upgrade to unlock all features.')}
              </p>

              <ul className="space-y-3 mb-8">
                {planFeatures.map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-[#C4C7C8]">
                    <Crown className="h-3.5 w-3.5 text-[#62FF96] shrink-0" />
                    {f.label}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => navigate('/plans')}
                className="w-full bg-white text-[#0A0A0B] font-bold py-3 rounded hover:opacity-90 transition-opacity text-sm"
                data-testid="upgrade-btn"
              >
                {isPremium
                  ? (language === 'hi' ? 'सब्सक्रिप्शन प्रबंधित करें' : 'Manage Subscription')
                  : (language === 'hi' ? 'प्रीमियम अपग्रेड करें' : 'Upgrade to Premium')}
              </button>
            </ExecCard>
          </div>

          {/* ── Change Password ── */}
          {user?.email && !user?.google_id && (
            <ExecCard>
              <div className="flex items-center gap-3 mb-6">
                <Lock className="h-5 w-5 text-[#8A8A93]" />
                <h3 className="text-lg font-bold text-[#F4F4F5]">
                  {language === 'hi' ? 'पासवर्ड बदलें' : 'Change Password'}
                </h3>
              </div>
              <form onSubmit={handleChangePassword} className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div className="space-y-2">
                  <label className="block text-xs font-bold tracking-widest uppercase text-[#8A8A93]">
                    {language === 'hi' ? 'वर्तमान पासवर्ड' : 'Current Password'}
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswords ? 'text' : 'password'}
                      value={passwordData.current_password}
                      onChange={(e) => setPasswordData(p => ({ ...p, current_password: e.target.value }))}
                      className="w-full p-3 pr-10 rounded-lg bg-[#050505] border border-[#262626] text-white text-sm outline-none focus:border-white transition-all"
                      data-testid="current-password-input"
                    />
                    <button type="button" onClick={() => setShowPasswords(!showPasswords)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A8A93]">
                      {showPasswords ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="block text-xs font-bold tracking-widest uppercase text-[#8A8A93]">
                    {language === 'hi' ? 'नया पासवर्ड' : 'New Password'}
                  </label>
                  <input type={showPasswords ? 'text' : 'password'} value={passwordData.new_password}
                    onChange={(e) => setPasswordData(p => ({ ...p, new_password: e.target.value }))}
                    className="w-full p-3 rounded-lg bg-[#050505] border border-[#262626] text-white text-sm outline-none focus:border-white transition-all"
                    data-testid="new-password-input" />
                </div>
                <div className="space-y-2">
                  <label className="block text-xs font-bold tracking-widest uppercase text-[#8A8A93]">
                    {language === 'hi' ? 'पासवर्ड की पुष्टि करें' : 'Confirm Password'}
                  </label>
                  <input type={showPasswords ? 'text' : 'password'} value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData(p => ({ ...p, confirm_password: e.target.value }))}
                    className="w-full p-3 rounded-lg bg-[#050505] border border-[#262626] text-white text-sm outline-none focus:border-white transition-all"
                    data-testid="confirm-password-input" />
                </div>
                <div className="md:col-span-3">
                  <button type="submit" disabled={savingPassword}
                    className="flex items-center gap-2 px-6 py-2.5 bg-white text-[#0A0A0B] text-xs font-bold tracking-widest uppercase rounded-lg hover:bg-white/90 active:scale-95 transition-all disabled:opacity-50"
                    data-testid="change-password-btn">
                    {savingPassword && <Loader2 className="h-4 w-4 animate-spin" />}
                    {language === 'hi' ? 'पासवर्ड बदलें' : 'Change Password'}
                  </button>
                </div>
              </form>
            </ExecCard>
          )}

          {/* ── Team Members ── */}
          {(user?.role === 'owner' || !user?.role) && (
            <div
              className="rounded-xl overflow-hidden"
              style={{ background: '#111111', border: '1px solid #262626' }}
            >
              <TeamMembers />
            </div>
          )}

          {/* ── QR Code Manager ── */}
          <div
            className="rounded-xl overflow-hidden"
            style={{ background: '#111111', border: '1px solid #262626' }}
          >
            <QRCodeManager />
          </div>

          {/* ── Bank Details ── */}
          <div
            className="rounded-xl overflow-hidden"
            style={{ background: '#111111', border: '1px solid #262626' }}
          >
            <BankDetails />
          </div>

          {/* ── Notifications + Localization ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Notification Engine */}
            <ExecCard>
              <div className="flex items-center gap-3 mb-6">
                <Bell className="h-5 w-5 text-[#8A8A93]" />
                <h3 className="text-lg font-bold text-[#F4F4F5]">
                  {language === 'hi' ? 'नोटिफिकेशन सेटिंग्स' : 'Notification Engine'}
                </h3>
              </div>
              <NotifRow
                label={language === 'hi' ? 'ईमेल नोटिफिकेशन' : 'Email Notifications'}
                sub={language === 'hi' ? 'चालान, रिपोर्ट और सुरक्षा अलर्ट।' : 'Invoices, monthly reports and critical security alerts.'}
                checked={notifications.email_notifications}
                onChange={(checked) => setNotifications(p => ({ ...p, email_notifications: checked }))}
                testId="email-notifications-switch"
              />
              <NotifRow
                label={language === 'hi' ? 'WhatsApp अपडेट' : 'WhatsApp Updates'}
                sub={language === 'hi' ? 'रियल-टाइम भुगतान पुष्टि।' : 'Real-time payment confirmations and transaction alerts.'}
                checked={notifications.whatsapp_notifications}
                onChange={(checked) => isPremium && setNotifications(p => ({ ...p, whatsapp_notifications: checked }))}
                disabled={!isPremium}
                testId="whatsapp-notifications-switch"
              />
              <NotifRow
                label={language === 'hi' ? 'कम स्टॉक अलर्ट' : 'Low Stock Alerts'}
                sub={language === 'hi' ? 'स्टॉक सीमा से कम होने पर अलर्ट।' : 'Get notified when inventory falls below thresholds.'}
                checked={notifications.low_stock_alerts}
                onChange={(checked) => isPremium && setNotifications(p => ({ ...p, low_stock_alerts: checked }))}
                disabled={!isPremium}
                testId="low-stock-alerts-switch"
              />
              <NotifRow
                label={language === 'hi' ? 'दैनिक सारांश' : 'Daily Summary'}
                sub={language === 'hi' ? 'सुबह 9 बजे पिछले दिन का प्रदर्शन।' : 'A consolidated view of previous day performance at 9:00 AM.'}
                checked={notifications.daily_summary}
                onChange={(checked) => setNotifications(p => ({ ...p, daily_summary: checked }))}
                testId="daily-summary-switch"
              />
            </ExecCard>

            {/* Localization + Onboarding */}
            <div className="space-y-5">
              <ExecCard>
                <div className="flex items-center gap-3 mb-5">
                  <Globe className="h-5 w-5 text-[#8A8A93]" />
                  <h3 className="text-lg font-bold text-[#F4F4F5]">
                    {language === 'hi' ? 'भाषा और स्थानीयकरण' : 'Localization'}
                  </h3>
                </div>
                <label className="block text-xs font-bold tracking-widest uppercase text-[#8A8A93] mb-3">
                  {language === 'hi' ? 'सिस्टम भाषा' : 'System Language'}
                </label>
                <Select value={language} onValueChange={(v) => setLanguage(v)}>
                  <SelectTrigger className="bg-[#050505] border-[#262626] text-white" data-testid="language-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English (India)</SelectItem>
                    <SelectItem value="hi">हिंदी (India)</SelectItem>
                  </SelectContent>
                </Select>
              </ExecCard>

              {/* Onboarding Tour */}
              <div
                className="flex items-center justify-between p-5 rounded-xl border border-dashed border-[#262626] cursor-pointer hover:bg-white/[0.02] transition-all"
                style={{ background: '#0D0D0D' }}
                onClick={() => window.replayOnboardingTour && window.replayOnboardingTour()}
              >
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-full bg-white/5 flex items-center justify-center">
                    <RocketIcon className="h-5 w-5 text-[#F4F4F5]" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-[#F4F4F5]">
                      {language === 'hi' ? 'ऑनबोर्डिंग और समर्थन' : 'Onboarding & Support'}
                    </p>
                    <p className="text-xs text-[#8A8A93]">
                      {language === 'hi' ? 'गाइडेड टूर फिर से देखें।' : 'Need a refresher? Replay the platform tour.'}
                    </p>
                  </div>
                </div>
                <button
                  className="text-xs font-bold text-[#F4F4F5] hover:text-white transition-colors"
                  data-testid="replay-tour-btn"
                >
                  {language === 'hi' ? 'टूर दोहराएं' : 'REPLAY TOUR'}
                </button>
              </div>

              {/* Mobile Logout */}
              <div className="lg:hidden">
                <button
                  onClick={logout}
                  className="w-full py-3 rounded-xl bg-red-900/30 border border-red-800/30 text-red-400 text-sm font-semibold hover:bg-red-900/50 transition-colors"
                  data-testid="mobile-logout-btn"
                >
                  {language === 'hi' ? 'लॉगआउट' : 'Logout'}
                </button>
              </div>
            </div>
          </div>
    </AetherPage>
  );
};

export default Profile;
